import Seo from "@/components/Seo";
import { localBusinessSchema, breadcrumbSchema } from "@/data/site";
import { PageHero, Section, SectionHead, CtaBand } from "@/components/sections";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion";
import { BirdMark } from "@/components/brand";
import { User } from "lucide-react";

const team = [
  { name: "Virginie", role: "Build & project lead", text: "Turns your idea into a working, tested product." },
  { name: "Danstand", role: "Client relationships", text: "Your main contact from first meeting to launch and beyond." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Sankofa Launch Studio — A Ghanaian Digital Agency"
        description="Sankofa Launch Studio is a Ghanaian digital agency that combines careful planning with fast, modern building tools to deliver websites, online shops and web apps for schools, hotels and businesses across Ghana."
        jsonLd={[
          localBusinessSchema(),
          breadcrumbSchema([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
        ]}
      />
      <PageHero
        eyebrow="About"
        breadcrumb="Home / About"
        title="Rooted in insight. Built for tomorrow."
        intro="Sankofa Launch Studio is a Ghanaian digital agency. The Sankofa bird — which teaches us to learn from the past to build the future — is at the heart of who we are."
      />

      <Section className="bg-cream">
        <div className="max-w-3xl space-y-6">
          <Reveal>
            <p className="text-lg leading-relaxed text-ink">
              We combine careful planning with fast, modern building tools to deliver websites, online shops and web apps
              that help Ghanaian schools, hotels and businesses grow.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="text-lg leading-relaxed text-ink">
              We believe in clear pricing, honest advice, and staying with our clients long after launch.
            </p>
          </Reveal>
        </div>
      </Section>

      {/* Meet the team */}
      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <SectionHead eyebrow="Meet the team" title="The people behind your project." />
          <StaggerGroup className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            {team.map((t) => (
              <StaggerItem key={t.name}>
                <article className="group flex items-center gap-6 rounded-2xl bg-cream p-6 shadow-soft md:p-8" data-testid={`team-${t.name.toLowerCase()}`}>
                  <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-2xl bg-navy/5 text-slate transition-colors group-hover:bg-navy group-hover:text-white md:h-28 md:w-28">
                    <User className="h-10 w-10" />
                  </div>
                  <div>
                    <h3 className="font-serif text-2xl font-600 text-navy">{t.name}</h3>
                    <p className="mt-1 text-sm font-600 uppercase tracking-wide text-gold">{t.role}</p>
                    <p className="mt-2 text-sm leading-relaxed text-ink">{t.text}</p>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <p className="mt-6 text-sm text-mutedink">Team photos coming soon — placeholders for now.</p>
        </div>
      </section>

      {/* Why Sankofa */}
      <Section className="bg-cream">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl bg-navy p-8 md:p-14" data-testid="why-sankofa">
            <img src="/assets/sankofa-bird.png" alt="" aria-hidden="true" className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 opacity-20 md:h-56 md:w-56" />
            <div className="relative flex items-center gap-3">
              <BirdMark className="h-9 w-9" alt="" />
              <h2 className="font-serif text-3xl font-600 text-white md:text-4xl">Why "Sankofa"?</h2>
            </div>
            <p className="relative mt-5 max-w-2xl leading-relaxed text-white/80">
              Sankofa is an Akan word and Adinkra symbol meaning "go back and get it." It teaches that there is wisdom in
              learning from the past to build a better future.
            </p>
            <p className="relative mt-4 max-w-2xl leading-relaxed text-white/80">
              That's exactly how we work: we begin every project by understanding your business and your people, then use
              modern tools to build something that carries you forward.
            </p>
          </div>
        </Reveal>
      </Section>

      <CtaBand title="Work with us" waText="Hello Sankofa, I'd like to work with your team." />
    </>
  );
}
